import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, ArrowRight } from 'lucide-react';
import type { NicheConfig } from '../../niches/types';

interface Props {
  config: NicheConfig;
}

export default function NicheHero({ config }: Props) {
  const { hero } = config;
  const isExternalCta = hero.ctaHref.startsWith('http');

  return (
    <section className="relative px-6 pt-10 md:pt-14 pb-16 md:pb-24 max-w-7xl mx-auto w-full">
      <div className="flex items-center mb-12 md:mb-20">
        <img src="/logoDaien.svg" alt="DAIEN" className="w-9 h-9" width="36" height="36" />
      </div>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="flex flex-col"
        >
          {hero.eyebrow && (
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-stroke"></div>
              <span className="text-xs text-muted uppercase tracking-[0.3em]">{hero.eyebrow}</span>
            </div>
          )}

          <h1 className="text-4xl md:text-5xl lg:text-[56px] font-normal leading-[1.05] tracking-tight text-text-primary mb-5">
            {hero.title}{' '}
            {hero.titleAccent && (
              <span className="bg-gradient-to-r from-[hsl(var(--accent-from))] via-[hsl(var(--accent-to))] to-text-primary bg-clip-text text-transparent">
                {hero.titleAccent}
              </span>
            )}
          </h1>

          <p className="text-base md:text-lg text-muted mb-8 max-w-xl leading-relaxed">
            {hero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/quiz"
              className="inline-flex items-center justify-center gap-2 bg-text-primary text-bg px-7 py-3.5 rounded-full text-base font-medium btn-animated"
            >
              Wypełnij quiz kwalifikacyjny
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={hero.ctaHref}
              target={isExternalCta ? '_blank' : undefined}
              rel={isExternalCta ? 'noopener noreferrer' : undefined}
              className="inline-flex items-center justify-center gap-2 border border-stroke text-text-primary px-7 py-3.5 rounded-full text-base font-medium hover:bg-surface transition-colors"
            >
              {hero.ctaLabel}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: 0.15 }}
          className="relative w-full aspect-video rounded-3xl overflow-hidden border border-stroke bg-surface shadow-xl"
        >
          {hero.vslEmbedUrl ? (
            <iframe
              src={hero.vslEmbedUrl}
              title="Video Sales Letter"
              className="absolute inset-0 w-full h-full"
              frameBorder={0}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center text-center p-8"
              style={{
                background:
                  'radial-gradient(circle at 30% 30%, hsl(var(--accent-from) / 0.25), transparent 60%), radial-gradient(circle at 70% 70%, hsl(var(--accent-to) / 0.2), transparent 60%), hsl(var(--surface))',
              }}
              aria-hidden="true"
            >
              <div className="w-20 h-20 rounded-full bg-text-primary/90 text-bg flex items-center justify-center mb-4 shadow-lg">
                <Play className="w-8 h-8 fill-current ml-1" />
              </div>
              <p className="text-sm uppercase tracking-[0.25em] text-muted">Video wkrótce</p>
              <p className="text-xs text-muted mt-2 max-w-xs">
                Nagrywam krótkie wideo z odpowiedziami na najczęstsze obiekcje
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
